import type { Cmyk, Color } from '@flobi/artwork-core';

/**
 * Colour in a CMYK file, as the chrome picks it.
 *
 * A colour is always stored with r, g and b — what the canvas draws — and in a CMYK file
 * also with `cmyk`, the ink. Two ways to arrive at one:
 *
 *   typed     the designer gave the ink (C, M, Y, K): kept exactly, and the screen colour
 *             is what those inks look like
 *   picked    anything else (wheel, square, a swatch, the eyedropper): the press's ink for
 *             it, and the screen colour is what THAT ink looks like — so what is shown is
 *             what prints, and a colour the press cannot make comes back as the nearest
 *             one it can
 *
 * In an RGB file nothing here runs, and a colour's stored ink is left alone.
 *
 * Pure: the press is passed in (the editor's is `ColorProofer`, loaded in ./press).
 */
export interface Press {
  toCmyk(color: Color): Cmyk;
  fromCmyk(ink: Cmyk): Color;
  proof(color: Color): Color;
}

/** Ink as the fields show it: whole percentages. */
export function inkPercent(ink: Cmyk): { c: number; m: number; y: number; k: number } {
  const pct = (value: number) => Math.round(Math.max(0, Math.min(1, value)) * 100);
  return { c: pct(ink.c), m: pct(ink.m), y: pct(ink.y), k: pct(ink.k) };
}

/** Percentages as ink, held to 0–100. */
export function inkFromPercent(percent: { c: number; m: number; y: number; k: number }): Cmyk {
  const unit = (value: number) => (Number.isFinite(value) ? Math.max(0, Math.min(100, value)) / 100 : 0);
  return { c: unit(percent.c), m: unit(percent.m), y: unit(percent.y), k: unit(percent.k) };
}

/** Ink said the way printers say it: "C0 M100 Y100 K0". */
export function inkLabel(ink: Cmyk): string {
  const p = inkPercent(ink);
  return `C${p.c} M${p.m} Y${p.y} K${p.k}`;
}

/** Ink in the short form printers write where room is tight: "0/100/100/0". */
export function inkShort(ink: Cmyk): string {
  const p = inkPercent(ink);
  return `${p.c}/${p.m}/${p.y}/${p.k}`;
}

/** A colour typed as ink: exactly that ink, shown as it prints. Alpha is the caller's. */
export function typedInk(press: Press, ink: Cmyk, alpha = 1): Color {
  const seen = press.fromCmyk(ink);
  return { r: seen.r, g: seen.g, b: seen.b, a: alpha, cmyk: { ...ink } };
}

/**
 * A colour picked any other way: the ink the press makes of its screen colour, and the
 * screen colour of that ink. Any ink it carried is ignored — the screen colour is what was
 * picked. Keeps its alpha.
 */
export function pickedInk(press: Press, color: Color): Color {
  const ink = press.toCmyk({ r: color.r, g: color.g, b: color.b, a: 1 });
  return typedInk(press, ink, color.a ?? 1);
}

/** The ink a stored colour stands for: what was typed, else what the press makes of it. */
export function inkOf(press: Press, color: Color): Cmyk {
  return color.cmyk ?? press.toCmyk(color);
}

/** A colour as a swatch should show it: as it prints in a CMYK file, as it is otherwise. */
export function shownColour(press: Press | null, color: Color): Color {
  return press ? press.proof(color) : color;
}

/** A colour from a hex (and the ink it was typed as, if any), with its alpha. */
export function colourFrom(hex: string, ink: Cmyk | undefined, alpha = 1): Color {
  const value = parseInt(hex.slice(1), 16);
  const color: Color = { r: ((value >> 16) & 255) / 255, g: ((value >> 8) & 255) / 255, b: (value & 255) / 255, a: alpha };
  return ink ? { ...color, cmyk: { ...ink } } : color;
}

/** Channels as #rrggbb. */
export function hexOf(color: { r: number; g: number; b: number }): string {
  const channel = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0');
  return `#${channel(color.r)}${channel(color.g)}${channel(color.b)}`;
}

/**
 * A hex picked in a CMYK file (a swatch, the wheel, a recent colour), as what the press
 * makes of it: the hex it shows as and the ink it prints with.
 */
export function pressHex(press: Press, hex: string): { hex: string; ink: Cmyk } {
  const color = pickedInk(press, colourFrom(hex, undefined));
  return { hex: hexOf(color), ink: color.cmyk! };
}

/** A hex as a swatch shows it: proofed in a CMYK file (with the ink it was typed as). */
export function shownHex(press: Press | null, hex: string, ink?: Cmyk): string {
  return press ? hexOf(press.proof(colourFrom(hex, ink))) : hex;
}
